import React, { FC, useState, FormEvent } from 'react';
import { Button, Input, Select, Switch, message, Form } from 'antd';
import * as translations from './strings';
import { getLangKey } from 'models/Language';
import HelpArticleButton from 'utils/HelpArticleButton';
import styles from './index.module.css';
import { createProject } from 'api/ProjectApi';
import { updateExtension } from 'appJS/utils/updateExtension';
import Icon from 'jsViews/onboarding/projectBoardIcon';
import {
  defaultProjectFields,
  displayColumnName,
  fieldNames,
  guestDefaultColumnName
} from 'appJS/views/settings/project_default/fields';

interface Props {
  organizationId: number;
  guestsSeeGuests: boolean;
  guestsEditTaskStatus: boolean;
  assignGuests: boolean;
  projectDefault?: { [key: string]: boolean | string };
  defaultColumns?: { name: string }[];
}

const { Item } = Form;

const strings = translations[getLangKey()];

const CreateNewProject: FC<Props> = props => {
  const {
    organizationId,
    guestsSeeGuests,
    guestsEditTaskStatus,
    projectDefault,
    assignGuests,
    defaultColumns = []
  } = props;
  const columnNames = defaultColumns.map(column => column.name).filter(Boolean);
  const projectDefaults = () => {
    if (!projectDefault) return {};

    return Object.keys(projectDefault).reduce((acc, key) => {
      switch (key) {
        case fieldNames.permission:
          acc[key] = !!guestsSeeGuests && projectDefault[key];
          break;
        case fieldNames.assign_guests:
          acc[key] = !!assignGuests && projectDefault[key];
          break;
        case fieldNames.allow_guests_change_task_status:
          acc[key] = !!guestsEditTaskStatus && projectDefault[key];
          break;
        default:
          acc[key] = projectDefault[key];
          break;
      }
      return acc;
    }, {});
  };
  const [projectName, setProjectName] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const savedColumnName = String(
    projectDefault?.[guestDefaultColumnName] || ''
  );
  const initialColumnName =
    columnNames.find(
      name => name.toLowerCase() === savedColumnName.toLowerCase()
    ) ||
    columnNames[0] ||
    '';
  const [settings, setSettings] = useState<{
    [key: string]: boolean | string;
  }>({
    ...defaultProjectFields,
    ...projectDefaults(),
    [guestDefaultColumnName]: initialColumnName
  });

  const [show, setShow] = useState(false);

  const submitProjectData = async () => {
    setSaving(true);

    try {
      const response = await createProject({
        organizationId,
        project: {
          name: projectName,
          permission: settings.permission
            ? 'guests_see_guests'
            : 'guests_see_self',
          allow_guests_change_task_status:
            settings.allow_guests_change_task_status,
          assign_guests: settings.assign_guests,
          allow_project_summary_email: settings.allow_project_summary_email,
          allow_task_done_email: settings.allow_task_done_email,
          change_guest_default_column: !!settings.change_guest_default_column,
          guest_default_column_name: String(
            settings[guestDefaultColumnName] || ''
          )
        }
      });

      if (response && response.status === 'ok') {
        updateExtension();
        window.location.pathname = `/projects/${response.project_id}/assets`;
      } else {
        message.error(strings.createError);
        setSaving(false);
      }
    } catch (error) {
      setSaving(false);
      console.error(error?.message);
    }
  };

  const getShow = (key: string) => {
    switch (key) {
      case fieldNames.permission:
        return guestsSeeGuests;
      case fieldNames.assign_guests:
        return assignGuests;
      case fieldNames.allow_guests_change_task_status:
        return guestsEditTaskStatus;
      default:
        return true;
    }
  };

  const switches = Object.keys(fieldNames).map(key => ({
    key,
    label: strings[key],
    show: getShow(key)
  }));

  const [form] = Form.useForm();

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    submitProjectData();
  };

  return (
    <div className={styles.containerCreateNewProject}>
      <div className={styles.flexContainerWrapper}>
        <div className={styles.contentWrapper}>
          <h2>{strings.createNewProject}</h2>
          <p className={styles.projectName}>{strings.nameLabel}:</p>
          <Form
            form={form}
            className={styles.inputButtonContainer}
            onFinish={submit}
          >
            <Form.Item
              name={'projectName'}
              initialValue={projectName}
              className={styles.nameField}
              rules={[
                {
                  type: 'string',
                  message: 'This is not a valid name'
                },
                {
                  required: true,
                  message: strings.emptyValidationMessage
                }
              ]}
            >
              <Input
                placeholder={strings.namePlaceholder}
                className={styles.nameInput}
                autoFocus
                onChange={event => setProjectName(event.target.value)}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className={styles.continueButton}
              loading={saving}
            >
              {strings.createProject}
            </Button>
          </Form>

          <div className={styles.switchContainer}>
            <div className={styles.optionsContainer}>
              {switches.slice(0, 2).map(
                ({ key, label, show }) =>
                  show && (
                    <Item key={key} className={styles.switchItem}>
                      <Switch
                        checked={!!settings[key]}
                        onChange={value =>
                          setSettings(prev => ({ ...prev, [key]: value }))
                        }
                        className={styles.switch}
                      />
                      <span className={styles.guestsMessage}>
                        {label}{' '}
                        <HelpArticleButton
                          articleId={strings.guestsHelpArticleId}
                        />
                      </span>
                    </Item>
                  )
              )}
            </div>

            <div className={styles.moreOptions}>
              <Button
                type="text"
                size="small"
                onClick={() => setShow(!show)}
                className={styles.changesButton}
              >
                {!show ? 'More client settings +' : 'Hide client settings -'}
              </Button>
              {show && (
                <>
                  {switches.slice(2, 6).map(
                    ({ key, label, show }) =>
                      show && (
                        <Item key={key} className={styles.switchItem}>
                          <Switch
                            checked={!!settings[key]}
                            onChange={value =>
                              setSettings(prev => ({
                                ...prev,
                                [key]: value,
                                ...(key ===
                                  fieldNames.change_guest_default_column &&
                                value &&
                                !prev[guestDefaultColumnName]
                                  ? {
                                      [guestDefaultColumnName]:
                                        columnNames[0] || ''
                                    }
                                  : {})
                              }))
                            }
                            className={styles.switch}
                          />
                          <span className={styles.guestsMessage}>
                            {label}{' '}
                            {key === fieldNames.change_guest_default_column &&
                              !!settings[guestDefaultColumnName] && (
                                <Select
                                  className={styles.columnSelect}
                                  disabled={!settings[key]}
                                  value={String(settings[guestDefaultColumnName])}
                                  options={columnNames.map(name => ({
                                    value: name,
                                    label: displayColumnName(name)
                                  }))}
                                  onChange={name =>
                                    setSettings(prev => ({
                                      ...prev,
                                      [guestDefaultColumnName]: name
                                    }))
                                  }
                                  popupMatchSelectWidth={false}
                                />
                              )}
                          </span>
                        </Item>
                      )
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.imageWrapper}>
          <Icon projectName={projectName} />
        </div>
      </div>
    </div>
  );
};

export default CreateNewProject;
